import { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Message } from '@copilotkit/shared';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import {
  CHAT_NAMESPACES,
  type GettingStartedCompletionState,
  type GettingStartedStepKey,
} from '@/types/aiChat';
import {
  appendUpgradeNudgeMessage,
  buildToolCallIdToNameMap,
} from '@/helpers/aiChat/messages';
import { useAITools } from '@/hooks/aiTools';
import { useMarkGettingStartedStepCompleteTool } from '@/hooks/aiTools/useMarkGettingStartedStepCompleteTool';
import { useGettingStartedAgentSync } from '@/hooks/aiTools/useGettingStartedAgentSync';
import {
  useCopilotKitAgent,
  GS_WELCOME_MESSAGE_KEY,
} from '@/hooks/aiChat/useCopilotKitAgent';
import { useSetupAssistantUpgradeState } from '@/hooks/aiChat/useSetupAssistantUpgradeState';
import { useAgentState } from '@/hooks/aiChat/useAgentState';
import { useDefaultToolRenderer } from '@/pages/AIChatCopilotKit/useDefaultToolRenderer';
import { useGettingStartedCompletionState } from '@/hooks/useGettingStartedCompletionState';
import { useUpdateGettingStartedProgress } from '@/pages/Dashboard/Sections/GettingStarted/hooks/useGettingStartedStorage';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';

/**
 * Registers the Getting Started setup assistant tools and message-stream sync.
 *
 * Extracted into its own component (rather than using an `enabled` prop) because
 * both hooks call React hooks internally — useCopilotAction in
 * useMarkGettingStartedStepCompleteTool and useEffect in useGettingStartedAgentSync —
 * which cannot be called conditionally at the call site. Mounting this component
 * only when the ithGettingStartedSetupAssistant flag is on keeps the agent's tool
 * context clean: the mark-step-complete tool is never registered when the feature
 * is off, so the agent cannot see or call it.
 */
const GsSetupAssistant = ({
  messages,
  needsHistoryRestore,
}: {
  messages: Message[];
  needsHistoryRestore: boolean;
}) => {
  const updateProgress = useUpdateGettingStartedProgress();
  useMarkGettingStartedStepCompleteTool({ updateProgress });
  useGettingStartedAgentSync({ messages, updateProgress, needsHistoryRestore });
  return null;
};

/**
 * Build a Set of tool function names that have a matching ToolMessage result
 * in the current messages array. Used to show restored tool call chips as
 * "completed" instead of "pending", since CopilotKit does not reconstruct
 * tool status from ToolMessage objects when setMessages is called.
 */
const computeCompletedToolNames = (messages: Message[]): Set<string> => {
  const idToName = buildToolCallIdToNameMap(messages);
  const names = new Set<string>();
  for (const msg of messages) {
    if (msg.role === 'tool' && 'toolCallId' in msg) {
      const name = idToName.get((msg as { toolCallId: string }).toolCallId);
      if (name) names.add(name);
    }
  }
  return names;
};

type CopilotKitAgentContextType = Pick<
  ReturnType<typeof useCopilotKitAgent>,
  | 'messages'
  | 'sendMessage'
  | 'setMessages'
  | 'isLoading'
  | 'isAvailable'
  | 'isCreatingNewConversation'
  | 'needsHistoryRestore'
  | 'setSuggestions'
  | 'pendingToolApproval'
  | 'hasPendingFrontendToolApproval'
  | 'respondToApproval'
  | 'runError'
  | 'agent'
> & {
  /** True while the initial conversation creation API call is in flight. */
  isInitializing: boolean;
  /** True when the initial conversation creation API call failed. */
  isError: boolean;
  /** True while a retry is in flight after a failed conversation creation. */
  isRetrying: boolean;
  /** Retry the conversation creation. No-op when isError is false. */
  retryCreateConversation: () => void;
};

// Exported so useNewChatAction can use useContext directly (nullable, no throw)
export const CopilotKitAgentContext =
  createContext<CopilotKitAgentContextType | null>(null);

/**
 * Provides a single CopilotKit agent instance to all consumers.
 *
 * By registering tools and creating the headless chat hook exactly once,
 * this prevents duplicate tool registration warnings and ensures a stable
 * `isAvailable` state when both the drawer and full-page views are mounted
 * simultaneously against the shared CopilotKit context.
 *
 * Must be rendered inside CopilotKitProvider (requires CopilotKit context).
 */
export const CopilotKitAgentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Setup-assistant-only orgs (free plan, temporary signal) get a scoped tool
  // set — see useAITools. Excludes orgs that also have full/basic Gigawatt.
  // TODO(TF-677): replace with gigawatt_setup_assistant entitlement once core-entity ships it
  const { isSetupAssistantOnlyOrg } = useAIChatFeatureFlags();

  // Register tools exactly once — prevents duplicate registration warnings
  // when both the drawer and full-page chat are mounted at the same time.
  useAITools(isSetupAssistantOnlyOrg);

  const { itHubDashboardGettingStarted, ithGettingStartedSetupAssistant } =
    useFlags();
  const isSuperAdmin = useIsSuperAdmin();
  const { t } = useTranslation('ai-chat');

  // Shared with useSetupAssistantUpgradeState below (via precomputedState) so
  // the ~8 queries/subscriptions inside useGettingStartedCompletionState run
  // once per render, not twice, for setup-assistant-only super admins.
  const isDashboardGsEnabled = !!itHubDashboardGettingStarted && isSuperAdmin;
  const {
    gettingStartedState,
    incompleteStepKeys,
    hasIncompleteSteps,
    isLoading: gsStateLoading,
  } = useGettingStartedCompletionState({
    // Not scoped to dashboard — the admin may navigate away while the agent is
    // helping them through setup steps, and the agent must retain GS context.
    enabled: isDashboardGsEnabled || isSetupAssistantOnlyOrg,
  });

  // Memoize on semantic content, not object references — useGettingStartedCompletionState
  // returns fresh gettingStartedState / incompleteStepKeys objects whenever any query
  // isFetching toggles (e.g. Employee tag invalidation after invite-admins), even when
  // the boolean flags inside are unchanged.
  const gettingStartedStateKey = useMemo(
    () => (gettingStartedState ? JSON.stringify(gettingStartedState) : ''),
    [gettingStartedState]
  );
  const incompleteStepKeysKey = useMemo(
    () => JSON.stringify(incompleteStepKeys),
    [incompleteStepKeys]
  );

  const additionalAgentState = useMemo(
    () => ({
      // Include GS state on any page once queries have settled.
      // while loading, step flags default to false/undefined and would mislead the agent.
      // Gated on isDashboardGsEnabled (not just gettingStartedStateKey) since the
      // underlying query can now also be enabled by isSetupAssistantOnlyOrg alone.
      ...(isDashboardGsEnabled &&
        !gsStateLoading &&
        gettingStartedStateKey && {
          getting_started_state: JSON.parse(
            gettingStartedStateKey
          ) as GettingStartedCompletionState,
          // Only expose incomplete steps when the setup assistant is active —
          // the agent has no use for them otherwise.
          ...(ithGettingStartedSetupAssistant && {
            getting_started_incomplete_steps: JSON.parse(
              incompleteStepKeysKey
            ) as GettingStartedStepKey[],
          }),
        }),
    }),
    [
      isDashboardGsEnabled,
      gsStateLoading,
      gettingStartedStateKey,
      incompleteStepKeysKey,
      ithGettingStartedSetupAssistant,
    ]
  );

  const agentState = useAgentState(
    CHAT_NAMESPACES.GIGAWATT_COPILOTKIT,
    additionalAgentState
  );

  // Only set the GS-aware initial message for super admins once loading is complete
  // and the setup assistant flag is on — hasIncompleteSteps is unreliable while queries are in-flight.
  const resolvedGsInitialMessage = useMemo(
    () =>
      itHubDashboardGettingStarted &&
      ithGettingStartedSetupAssistant &&
      isSuperAdmin &&
      !gsStateLoading &&
      hasIncompleteSteps
        ? t(GS_WELCOME_MESSAGE_KEY)
        : undefined,
    [
      itHubDashboardGettingStarted,
      ithGettingStartedSetupAssistant,
      isSuperAdmin,
      gsStateLoading,
      hasIncompleteSteps,
      t,
    ]
  );

  // Hold the last resolved GS welcome while queries refetch so the welcome-message
  // sync effect doesn't ping-pong generic ↔ GS text during Employee invalidation.
  const stickyGsInitialMessageRef = useRef<string | undefined>(undefined);
  if (!gsStateLoading) {
    stickyGsInitialMessageRef.current = resolvedGsInitialMessage;
  }
  const gsInitialMessage = gsStateLoading
    ? stickyGsInitialMessageRef.current
    : resolvedGsInitialMessage;

  // useCopilotKitAgent must be called before useDefaultToolRenderer so that
  // pendingToolApproval and respondToApproval are available to pass as options.
  const {
    messages,
    sendMessage,
    setMessages,
    isLoading,
    isAvailable,
    isCreatingNewConversation,
    needsHistoryRestore,
    setSuggestions,
    pendingToolApproval,
    hasPendingFrontendToolApproval,
    respondToApproval,
    runError,
    agent,
  } = useCopilotKitAgent(CHAT_NAMESPACES.GIGAWATT_COPILOTKIT, agentState, {
    initialMessage: gsInitialMessage,
  });

  // Track which tool names have a confirmed ToolMessage result so that restored
  // chips show "completed" rather than "pending". Memoized on messages so it
  // only recomputes when the message list actually changes, not on every render.
  const completedToolNames = useMemo(
    () => computeCompletedToolNames(messages),
    [messages]
  );
  const completedToolNamesRef = useRef<Set<string>>(new Set());
  completedToolNamesRef.current = completedToolNames;

  useDefaultToolRenderer({
    pendingToolApproval,
    respondToApproval,
    completedToolNamesRef,
  });

  // Setup-assistant-only orgs have no ongoing Gigawatt entitlement once every
  // Getting Started step is done — the backend agent would otherwise keep
  // responding as a general help desk with nothing backing that access.
  // Shared with TopNavBase's nudge-on-open check and the setup assistant's
  // Upgrade CTA so all three call sites agree on "setup is fully complete".
  const { setupFullyComplete } = useSetupAssistantUpgradeState({
    gettingStartedState,
    hasIncompleteSteps,
    isLoading: gsStateLoading,
  });

  const guardedSendMessage: typeof sendMessage = useCallback(
    (message, options) => {
      if (!setupFullyComplete) {
        return sendMessage(message, options);
      }

      // Static, client-side-only nudge — never reaches the backend, since
      // these orgs have no ongoing Gigawatt entitlement to spend on general chat.
      // appendUpgradeNudgeMessage shares UPGRADE_NUDGE_MESSAGE_ID with
      // TopNavBase's nudge-on-open so whichever path shows it first, the
      // other doesn't append a duplicate.
      agent?.addMessage(message);
      appendUpgradeNudgeMessage(agent, t('upgradeNudgeMessage'));
      return Promise.resolve();
    },
    [setupFullyComplete, sendMessage, agent, t]
  );

  const contextValue = useMemo(
    () => ({
      messages,
      sendMessage: guardedSendMessage,
      setMessages,
      isLoading,
      isAvailable,
      isCreatingNewConversation,
      needsHistoryRestore,
      setSuggestions,
      pendingToolApproval,
      hasPendingFrontendToolApproval,
      respondToApproval,
      runError,
      agent,
      isInitializing: false,
      isError: false,
      isRetrying: false,
      retryCreateConversation: () => {},
    }),
    [
      messages,
      guardedSendMessage,
      setMessages,
      isLoading,
      isAvailable,
      isCreatingNewConversation,
      needsHistoryRestore,
      setSuggestions,
      agent,
      pendingToolApproval,
      hasPendingFrontendToolApproval,
      respondToApproval,
      runError,
    ]
  );

  return (
    <CopilotKitAgentContext.Provider value={contextValue}>
      {ithGettingStartedSetupAssistant && (
        <GsSetupAssistant
          messages={messages}
          needsHistoryRestore={needsHistoryRestore}
        />
      )}
      {children}
    </CopilotKitAgentContext.Provider>
  );
};

type CopilotKitAgentErrorProviderProps = {
  children: ReactNode;
  retry: () => void;
  isRetrying: boolean;
};

/**
 * Provides a degraded agent context when the initial conversation creation fails.
 *
 * The app renders normally (routes, nav) while chat components see isError=true
 * and display an inline error with a retry button instead of crashing.
 *
 * Rendered by CopilotKitProvider in the error path — does NOT require CopilotKit context.
 */
export const CopilotKitAgentErrorProvider = ({
  children,
  retry,
  isRetrying,
}: CopilotKitAgentErrorProviderProps) => {
  // Stable context value — only changes when retry or isRetrying changes.
  const contextValue = useMemo(
    () => ({
      messages: [],
      sendMessage: async () => {},
      setMessages: () => {},
      isLoading: false,
      isAvailable: false,
      isCreatingNewConversation: false,
      needsHistoryRestore: false,
      setSuggestions: () => {},
      pendingToolApproval: null,
      hasPendingFrontendToolApproval: false,
      respondToApproval: () => {},
      runError: null,
      agent: undefined,
      isInitializing: false,
      isError: true,
      isRetrying,
      retryCreateConversation: retry,
    }),
    [retry, isRetrying]
  );

  return (
    <CopilotKitAgentContext.Provider value={contextValue}>
      {children}
    </CopilotKitAgentContext.Provider>
  );
};

/**
 * Provides a degraded agent context while the initial conversation is being created.
 *
 * The app renders normally (routes, nav) while chat components see isInitializing=true
 * and display an inline loading state instead of the full chat interface.
 *
 * Rendered by CopilotKitProvider in the loading path — does NOT require CopilotKit context.
 */
export const CopilotKitAgentLoadingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const contextValue = useMemo(
    () => ({
      messages: [],
      sendMessage: async () => {},
      setMessages: () => {},
      isLoading: false,
      isAvailable: false,
      isCreatingNewConversation: false,
      setSuggestions: () => {},
      pendingToolApproval: null,
      hasPendingFrontendToolApproval: false,
      respondToApproval: () => {},
      runError: null,
      agent: undefined,
      needsHistoryRestore: false,
      isInitializing: true,
      isError: false,
      isRetrying: false,
      retryCreateConversation: () => {},
    }),
    []
  );

  return (
    <CopilotKitAgentContext.Provider value={contextValue}>
      {children}
    </CopilotKitAgentContext.Provider>
  );
};

/**
 * Consume the shared CopilotKit agent state.
 * Must be called inside a component rendered within CopilotKitAgentProvider.
 */
export const useCopilotKitAgentContext = (): CopilotKitAgentContextType => {
  const ctx = useContext(CopilotKitAgentContext);
  if (!ctx) {
    throw new Error(
      'useCopilotKitAgentContext must be used within CopilotKitAgentProvider'
    );
  }
  return ctx;
};
